/**
 * Aba "Clientes" dentro de /app/propostas — lista TODOS os clientes
 * com indicador de quantas propostas têm.
 */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Search, FileText, Building2, ExternalLink, Loader2 } from "lucide-react";

export default function PropostasAbaClientes() {
  const [clientes, setClientes] = useState([]);
  const [propostasPorCliente, setPropostasPorCliente] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filtroUnidade, setFiltroUnidade] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [cliRes, propRes] = await Promise.all([
          api.get("/clientes?limit=1000"),
          api.get("/proposta-nativa?limit=1000"),
        ]);
        const cliList = Array.isArray(cliRes.data) ? cliRes.data : cliRes.data?.items || [];
        setClientes(cliList);
        // Agrupa propostas por cliente_id ou cliente_email
        const map = {};
        for (const p of propRes.data?.items || []) {
          const key = p.cliente_id || p.cliente_email;
          if (!key) continue;
          if (!map[key]) map[key] = { total: 0, ultimo_status: null, ultima_data: null };
          map[key].total += 1;
          if (!map[key].ultima_data || (p.criado_em && p.criado_em > map[key].ultima_data)) {
            map[key].ultima_data = p.criado_em;
            map[key].ultimo_status = p.status;
          }
        }
        setPropostasPorCliente(map);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    let arr = clientes;
    if (q.trim()) {
      const ql = q.toLowerCase();
      arr = arr.filter(
        (c) =>
          (c.nome || "").toLowerCase().includes(ql) ||
          (c.cnpj_cpf || "").includes(ql) ||
          (c.email || "").toLowerCase().includes(ql)
      );
    }
    if (filtroUnidade) {
      arr = arr.filter((c) =>
        filtroUnidade === "__none__" ? !c.unidade_id : c.unidade_id === filtroUnidade
      );
    }
    return arr;
  }, [clientes, q, filtroUnidade]);

  const stats = useMemo(() => {
    let comProposta = 0;
    let semProposta = 0;
    for (const c of clientes) {
      const key = c.id;
      if (propostasPorCliente[key] || propostasPorCliente[c.email]) comProposta += 1;
      else semProposta += 1;
    }
    return { total: clientes.length, comProposta, semProposta };
  }, [clientes, propostasPorCliente]);

  return (
    <div className="space-y-4" data-testid="propostas-aba-clientes">
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground tracking-wide">Total clientes</div>
          <div className="text-2xl font-bold mt-1" data-testid="kpi-cli-total">{stats.total}</div>
        </Card>
        <Card className="p-4 border-emerald-200">
          <div className="text-xs uppercase text-emerald-700 tracking-wide">Com proposta</div>
          <div className="text-2xl font-bold mt-1 text-emerald-700">{stats.comProposta}</div>
        </Card>
        <Card className="p-4 border-amber-200">
          <div className="text-xs uppercase text-amber-700 tracking-wide">Sem proposta ainda</div>
          <div className="text-2xl font-bold mt-1 text-amber-700">{stats.semProposta}</div>
        </Card>
      </div>

      <Card className="p-3 flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="aba-cli-busca"
            placeholder="Buscar por nome, CNPJ/CPF, e-mail…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-9 text-sm"
          />
        </div>
        <select
          data-testid="aba-cli-filtro-unidade"
          value={filtroUnidade}
          onChange={(e) => setFiltroUnidade(e.target.value)}
          className="h-9 px-3 text-sm border rounded-md bg-white"
        >
          <option value="">Todas unidades</option>
          <option value="__none__">Sem unidade</option>
          <option value="un_recife">Recife/PE</option>
          <option value="un_fortaleza">Fortaleza/CE</option>
          <option value="un_natal">Natal/RN</option>
        </select>
        <Badge variant="outline" className="text-xs">{filtered.length} resultado{filtered.length === 1 ? "" : "s"}</Badge>
      </Card>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Cliente</TableHead>
              <TableHead>CNPJ/CPF</TableHead>
              <TableHead>Unidade</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Propostas</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8">
                  <Loader2 className="inline animate-spin" size={14} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground text-sm">
                  Nenhum cliente encontrado com esses filtros.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((c) => {
                const info = propostasPorCliente[c.id] || propostasPorCliente[c.email];
                return (
                  <TableRow key={c.id} data-testid={`aba-cli-row-${c.id}`}>
                    <TableCell className="font-medium">{c.nome}</TableCell>
                    <TableCell className="font-mono text-xs">{c.cnpj_cpf || "—"}</TableCell>
                    <TableCell className="text-xs">
                      {c.unidade_id ? (
                        <Badge variant="outline" className="text-[10px]">
                          <Building2 size={10} className="mr-1" />
                          {c.unidade_id.replace("un_", "")}
                        </Badge>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{c.status || "em_analise"}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {info ? (
                        <Badge className="bg-emerald-100 text-emerald-800">
                          <FileText size={10} className="mr-1" /> {info.total}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={`/app/clientes/${c.id}`}>
                        <Button size="sm" variant="outline" data-testid={`aba-cli-abrir-${c.id}`}>
                          <ExternalLink size={12} className="mr-1" /> Abrir
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
